import React, { useState, useEffect, Fragment } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import api from "../../../api";
import swal from "sweetalert";
import { Row, Col, Card, Table, Button } from "react-bootstrap";
import PageTitle from "../../layouts/PageTitle";
import { useRef } from "react";
import BoqImportModal from "./BoqImportModal";
import WorkOrderUserAssignment from "./WorkOrderUserAssignment";

const WorkOrderAdd = () => {
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const projectFromUrl = searchParams.get("project") || "";
    const [contacts, setContacts] = useState([]);
    const [users, setUsers] = useState([]);
    const [documents, setDocuments] = useState([]);
    const [loading, setLoading] = useState(false);
    const locationRef = useRef(null);
    const [documentTypes, setDocumentTypes] = useState([]);
    const [loadingTypes, setLoadingTypes] = useState(false);
    const [projects, setProjects] = useState([]);
    const [offices, setOffices] = useState([]);
    const [scopeTypes, setScopeTypes] = useState([]);
    const [loadingScopes, setLoadingScopes] = useState(false);
    const [showBoqImport, setShowBoqImport] = useState(false);

    useEffect(() => {
        const fetchData = async () => {
            try {
                const [clientsRes, usersRes, projectsRes, scopeRes, officeRes] = await Promise.all(
                    [
                        api.get("/clients"),
                        api.get("/users?limit=1000"),
                        api.get("/projects?limit=1000"),
                        api.get("/scope-types"),
                        api.get("/offices").catch(() => ({ data: { success: false, data: [] } })),
                    ],
                );
                if (scopeRes.data.success) {
                    setScopeTypes(scopeRes.data.data);
                }

                if (clientsRes.data.success) setContacts(clientsRes.data.data);
                if (usersRes.data.success) setUsers(usersRes.data.data);
                if (officeRes?.data?.success && Array.isArray(officeRes.data.data)) {
                    setOffices(officeRes.data.data);
                }

                if (projectsRes.data.success) {
                    const projectList = projectsRes.data.data;
                    setProjects(projectList);

                    // ⭐ Auto select project if coming from filter
                    if (projectFromUrl) {
                        const selectedProject = projectList.find(
                            (p) => String(p._id) === String(projectFromUrl)
                        );
                        const projectOffice = selectedProject?.office;
                        const officeState = projectOffice?.state || "";

                        let clientId = "";
                        let clientContactNumber = "";
                        let clientEmail = "";
                        if (selectedProject?.client) {
                            const projClientId = selectedProject.client?._id || selectedProject.client;
                            const matchedClient = (clientsRes.data?.data || []).find(
                                (c) => String(c._id) === String(projClientId)
                            );
                            if (matchedClient) {
                                clientId = String(matchedClient._id);
                                clientContactNumber = matchedClient.mobile || "";
                                clientEmail = matchedClient.gmail || matchedClient.email || "";
                            }
                        }

                        const selectedClient = (clientsRes.data?.data || []).find(
                            (c) => String(c._id) === String(clientId)
                        );
                        const clientState =
                            selectedClient?.billingAddress?.state ||
                            selectedClient?.shippingAddress?.state ||
                            selectedClient?.state ||
                            "";

                        const { cgst, sgst, igst } = applyGSTByState(clientState, officeState);

                        setForm((prev) => ({
                            ...prev,
                            project: projectFromUrl,
                            client: clientId,
                            clientContactNumber,
                            clientEmail,
                            cgst,
                            sgst,
                            igst,
                        }));
                    }
                }
            } catch (error) {
                swal("Error", error.message || "Failed to load data", "error");
            }
        };

        fetchData();
    }, []);

    const [form, setForm] = useState({
        woNo: "",
        label: "",
        client: "",
        project: "",
        scope: "", // description text
        scopeTypes: [], // ✅ checkbox scope
        value: "",
        status: "Pending",
        startDate: "",
        endDate: "",
        subTotal: "",
        cgst: 0,
        sgst: 0,
        igst: 0,
        cgstAmount: 0,
        sgstAmount: 0,
        igstAmount: 0,
        totalAmount: 0,
        // 🔥 CLIENT AUTO-FILL
        clientContactNumber: "",
        clientEmail: "",
        projectManager: "",
        engineerAssigned: [],
        // 🔥 EXECUTION PLANNING
        estimatedManpower: "",
        peakManpower: "",
        materialBudget: "",
        labourBudget: "",
        contractBudget: "",
        otherBudget: "",
        otherBudgetNote: "",
        sacCode: "995461",
        boqItems: [],
    });

    const [gstNotIncluded, setGstNotIncluded] = useState([]);

    const handleAddGstNotIncluded = () => {
        setGstNotIncluded((prev) => [
            ...prev,
            { id: Date.now() + Math.random(), title: "", amount: "" },
        ]);
    };

    const handleGstNotIncludedChange = (id, field, val) => {
        setGstNotIncluded((prev) =>
            prev.map((item) => (item.id === id ? { ...item, [field]: val } : item))
        );
    };

    const handleRemoveGstNotIncluded = (id) => {
        setGstNotIncluded((prev) => prev.filter((item) => item.id !== id));
    };

    const calculateDays = () => {
        if (!form.startDate || !form.endDate) return "";

        const start = new Date(form.startDate);
        const end = new Date(form.endDate);

        if (end < start) return "";

        const diffTime = end.getTime() - start.getTime();
        const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24)) + 1;

        return diffDays;
    };

    const handleChange = (e) => {
        const { name, value } = e.target;
        setForm((prev) => ({ ...prev, [name]: value }));
    };

    const normalizeState = (s) => (s || "").toString().trim().toLowerCase();

    const getClientState = (client) => {
        return (
            client?.billingAddress?.state ||
            client?.shippingAddress?.state ||
            client?.state ||
            ""
        );
    };

    const getProjectOffice = (project) => {
        if (!project) return null;
        if (project.office && typeof project.office === "object" && (project.office.state || project.office.officeName)) {
            return project.office;
        }
        const officeId = project.office?._id || project.office;
        if (officeId && offices.length) {
            return offices.find((o) => String(o._id) === String(officeId)) || null;
        }
        return null;
    };

    const getOfficeState = (project) => {
        const office = getProjectOffice(project);
        return office?.state || "";
    };

    const applyGSTByState = (clientState, officeState) => {
        let cgst = 0,
            sgst = 0,
            igst = 0;

        if (!clientState || !officeState) {
            // Unknown state → safest is IGST
            igst = 18;
            return { cgst, sgst, igst };
        }

        const cState = normalizeState(clientState);
        const oState = normalizeState(officeState);

        if (cState === oState) {
            cgst = 9;
            sgst = 9;
            igst = 0;
        } else {
            cgst = 0;
            sgst = 0;
            igst = 18;
        }

        return { cgst, sgst, igst };
    };

    const handleClientChange = (e) => {
        const selectedClientId = e.target.value;

        if (!selectedClientId) {
            setForm((prev) => ({
                ...prev,
                client: "",
                clientContactNumber: "",
                clientEmail: "",
                cgst: 0,
                sgst: 0,
                igst: 0,
            }));
            return;
        }

        const selectedClient = contacts.find(
            (c) => String(c._id) === String(selectedClientId)
        );
        const clientState = getClientState(selectedClient);

        const selectedProject = projects.find(
            (p) => String(p._id) === String(form.project)
        );
        const officeState = getOfficeState(selectedProject);

        const { cgst, sgst, igst } = applyGSTByState(clientState, officeState);

        setForm((prev) => ({
            ...prev,
            client: selectedClientId,
            clientContactNumber: selectedClient?.mobile || "",
            clientEmail: selectedClient?.gmail || selectedClient?.email || "",
            cgst,
            sgst,
            igst,
        }));
    };

    const handleProjectChange = (e) => {
        const selectedProjectId = e.target.value;
        const selectedProject = projects.find(
            (p) => String(p._id) === String(selectedProjectId)
        );
        const officeState = getOfficeState(selectedProject);

        let clientId = form.client;
        let clientContactNumber = form.clientContactNumber;
        let clientEmail = form.clientEmail;

        // Auto-select client from project if client not already chosen
        if (!clientId && selectedProject?.client) {
            const projClientId = selectedProject.client?._id || selectedProject.client;
            const matchedClient = contacts.find(
                (c) => String(c._id) === String(projClientId)
            );
            if (matchedClient) {
                clientId = String(matchedClient._id);
                clientContactNumber = matchedClient.mobile || "";
                clientEmail = matchedClient.gmail || matchedClient.email || "";
            }
        }

        const selectedClient = contacts.find(
            (c) => String(c._id) === String(clientId)
        );
        const clientState = getClientState(selectedClient);

        const { cgst, sgst, igst } = applyGSTByState(clientState, officeState);

        setForm((prev) => ({
            ...prev,
            project: selectedProjectId,
            client: clientId,
            clientContactNumber,
            clientEmail,
            engineerAssigned: [],
            cgst,
            sgst,
            igst,
        }));
    };

    useEffect(() => {
        if (!form.client || !form.project) return;

        const selectedClient = contacts.find(
            (c) => String(c._id) === String(form.client)
        );
        const clientState = getClientState(selectedClient);

        const selectedProject = projects.find(
            (p) => String(p._id) === String(form.project)
        );
        const officeState = getOfficeState(selectedProject);

        if (clientState && officeState) {
            const { cgst, sgst, igst } = applyGSTByState(clientState, officeState);
            setForm((prev) => {
                if (prev.cgst === cgst && prev.sgst === sgst && prev.igst === igst) {
                    return prev;
                }
                return {
                    ...prev,
                    cgst,
                    sgst,
                    igst,
                };
            });
        }
    }, [form.client, form.project, contacts, projects, offices]);

    useEffect(() => {
        const projectValue = Number(form.value || 0);
        const nonGstTotal = (gstNotIncluded || []).reduce(
            (sum, item) => sum + (Number(item.amount) || 0),
            0
        );
        const taxableSubTotal = Math.max(0, projectValue - nonGstTotal);

        const cgstAmount = (taxableSubTotal * form.cgst) / 100;
        const sgstAmount = (taxableSubTotal * form.sgst) / 100;
        const igstAmount = (taxableSubTotal * form.igst) / 100;

        const total = projectValue + cgstAmount + sgstAmount + igstAmount;

        setForm((prev) => ({
            ...prev,
            subTotal: taxableSubTotal,
            cgstAmount,
            sgstAmount,
            igstAmount,
            totalAmount: total,
        }));
    }, [form.value, form.cgst, form.sgst, form.igst, gstNotIncluded]);
    const valueNumber = Number(form.value || 0);

    const formatIndianCurrency = (num) => {
        return new Intl.NumberFormat("en-IN", {
            maximumFractionDigits: 2,
        }).format(num);
    };

    const getIndianValueText = (num) => {
        if (!num) return "";

        if (num < 1000) return `${num}`;
        if (num < 100000) return `${(num / 1000).toFixed(2)} Thousand`;
        if (num < 10000000) return `${(num / 100000).toFixed(2)} Lakh`;
        return `${(num / 10000000).toFixed(2)} Crore`;
    };

    const formatIndianNumber = (num) => {
        if (!num) return "";
        return new Intl.NumberFormat("en-IN").format(num);
    };
    useEffect(() => {
        fetchDocumentTypes();
    }, []);

    const fetchDocumentTypes = async () => {
        try {
            setLoadingTypes(true);
            const res = await api.get("/document-types?includeInactive=false");
            if (res.data.success) {
                setDocumentTypes(res.data.data);
            }
        } catch (err) {
            console.error("Failed to load document types:", err);
            swal("Error", "Failed to load document types", "error");
        } finally {
            setLoadingTypes(false);
        }
    };

    useEffect(() => {
        if (!window.google || !locationRef.current) return;

        const autocomplete = new window.google.maps.places.Autocomplete(
            locationRef.current,
            {
                types: ["geocode"],
                componentRestrictions: { country: "in" }, // India only
            },
        );

        autocomplete.addListener("place_changed", () => {
            const place = autocomplete.getPlace();
            if (!place.geometry) return;

            setForm((prev) => ({
                ...prev,
                location: place.formatted_address,
                latitude: place.geometry.location.lat(),
                longitude: place.geometry.location.lng(),
            }));
        });
    }, []);

    const handleFileUpload = (e) => {
        const files = Array.from(e.target.files);

        const mapped = files.map((file, index) => ({
            id: `doc-${Date.now()}-${index}`,
            file,
            documentType: "",
            remarks: "",
        }));

        setDocuments((prev) => [...prev, ...mapped]);
    };

    const handleDocumentChange = (id, field, value) => {
        setDocuments((prev) =>
            prev.map((d) => (d.id === id ? { ...d, [field]: value } : d)),
        );
    };

    const handleRemoveDocument = (id) => {
        swal({
            title: "Are you sure?",
            text: "This document will be removed!",
            icon: "warning",
            buttons: true,
            dangerMode: true,
        }).then((ok) => {
            if (ok) {
                setDocuments((prev) => prev.filter((d) => d.id !== id));
            }
        });
    };

    const handleApplyImportedBoq = (importedItems) => {
        if (!Array.isArray(importedItems) || importedItems.length === 0) {
            swal("Warning", "No BOQ rows were parsed", "warning");
            return;
        }

        setForm((prev) => ({ ...prev, boqItems: importedItems }));
        swal(
            "Imported",
            `${importedItems.length} BOQ rows loaded into the work order form.`,
            "success",
        );
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);

        // =========================
        // 🔍 DOCUMENT VALIDATION
        // =========================
        for (const doc of documents) {
            if (!doc.documentType) {
                swal(
                    "Warning",
                    "Please select document type for all attachments",
                    "warning",
                );
                setLoading(false);
                return;
            }
        }

        // =========================
        // 🔍 SCOPE VALIDATION
        // =========================
        if (form.scopeTypes.length === 0) {
            swal("Warning", "Please select at least one scope of work", "warning");
            setLoading(false);
            return;
        }

        if (!form.scope || !form.scope.trim()) {
            swal("Warning", "Scope description is required", "warning");
            setLoading(false);
            return;
        }

        // =========================
        // 🔍 REQUIRED FIELDS
        // =========================
        if (!form.client || !form.project) {
            swal("Warning", "Please fill all required fields", "warning");
            setLoading(false);
            return;
        }

        // =========================
        // 🔍 VALUE (OPTIONAL - WILL BE DERIVED FROM BOQ)
        // =========================
        const workOrderValue = Number(form.value || 0);

        // =========================
        // 🔍 DATE VALIDATION
        // =========================
        if (!form.startDate || !form.endDate) {
            swal("Warning", "Start Date and End Date are required", "warning");
            setLoading(false);
            return;
        }

        if (new Date(form.startDate) >= new Date(form.endDate)) {
            swal("Warning", "End Date must be after Start Date", "warning");
            setLoading(false);
            return;
        }

        try {
            const fd = new FormData();

            // =========================
            // 📦 FORM DATA
            // =========================
            Object.keys(form).forEach((key) => {
                if (key === "scope" || key === "boqItems") return;

                const value = form[key];

                if (Array.isArray(value)) {
                    value.forEach((v) => fd.append(key, v));
                } else if (value !== "" && value !== null && value !== undefined) {
                    fd.append(key, value);
                }
            });

            // ✅ IMPORTANT: Backend expects `description`
            fd.append("description", form.scope);
            fd.set("boqItems", JSON.stringify(form.boqItems || []));
            fd.set("engineerAssignedJson", JSON.stringify(form.engineerAssigned || []));

            // =========================
            // 📐 DERIVED VALUES
            // =========================
            fd.set("value", workOrderValue);
            fd.append("valueText", getIndianValueText(workOrderValue));
            fd.append("totalDays", calculateDays());
            fd.append("subTotal", Number(form.subTotal || 0));
            fd.set(
                "gstNotIncluded",
                JSON.stringify(
                    (gstNotIncluded || [])
                        .map((i) => ({
                            title: (i.title || "").trim(),
                            amount: Number(i.amount) || 0,
                        }))
                        .filter((i) => i.title || i.amount > 0)
                )
            );


            // =========================
            // 🔥 BUDGET SUBMISSION
            // =========================
            fd.set("estimatedManpower", Number(form.estimatedManpower || 0));
            fd.set("peakManpower", Number(form.peakManpower || 0));
            fd.set("materialBudget", Number(form.materialBudget || 0));
            fd.set("labourBudget", Number(form.labourBudget || 0));
            fd.set("contractBudget", Number(form.contractBudget || 0));
            fd.set("otherBudget", Number(form.otherBudget || 0));
            fd.set("otherBudgetNote", form.otherBudgetNote || "");
            // totalBudget is auto-calculated on backend

            // =========================
            // 📎 DOCUMENTS
            // =========================
            documents.forEach((doc, index) => {
                fd.append("attachments", doc.file);
                fd.append(`attachmentTypes[${index}]`, doc.documentType);
                fd.append(`attachmentRemarks[${index}]`, doc.remarks || "");
            });

            // =========================
            // 🚀 API CALL
            // =========================
            const res = await api.post("/work-orders", fd, {
                headers: { "Content-Type": "multipart/form-data" },
            });

            if (res.data.success) {
                swal("Success", "Work Order Created!", "success");
                navigate(
                    projectFromUrl
                        ? `/work-orders?project=${projectFromUrl}`
                        : "/work-orders"
                );
            }
        } catch (err) {
            swal(
                "Error",
                err.response?.data?.message || "Failed to create Work Order",
                "error",
            );
        } finally {
            setLoading(false);
        }
    };

    return (
        <Fragment>
            <PageTitle activeMenu="Add Work Order" motherMenu="Work Orders" />

            <Row>
                <Col lg={12}>
                    <Card>
                        <Card.Header className="d-flex justify-content-between">
                            <Card.Title>Add Work Order</Card.Title>
                            <Button
                                variant="secondary"
                                onClick={() =>
                                    navigate(
                                        projectFromUrl
                                            ? `/work-orders?project=${projectFromUrl}`
                                            : "/work-orders"
                                    )
                                }
                            >
                                Back
                            </Button>
                        </Card.Header>

                        <Card.Body>
                            <form onSubmit={handleSubmit}>
                                <div className="row mb-3">
                                    <div className="col-md-4">
                                        <label>WO Number *</label>
                                        <input
                                            type="text"
                                            name="woNo"
                                            className="form-control"
                                            value={form.woNo}
                                            onChange={handleChange}
                                            placeholder="Enter Work Order Number"
                                        />
                                    </div>
                                    <div className="col-md-4">
                                        <label>Work Order Label / Name</label>
                                        <input
                                            type="text"
                                            name="label"
                                            className="form-control"
                                            value={form.label}
                                            onChange={handleChange}
                                            placeholder="e.g. Phase 1 - Civil Work"
                                        />
                                    </div>
                                    <div className="col-md-4">
                                        <label>SAC Code *</label>
                                        <input
                                            type="text"
                                            name="sacCode"
                                            className="form-control"
                                            value={form.sacCode}
                                            onChange={handleChange}
                                            placeholder="Enter SAC Code"
                                        />
                                    </div>
                                </div>

                                <div className="row mb-3">
                                    {/* CLIENT */}
                                    <div className="col-md-4">
                                        <label>Client *</label>
                                        <select
                                            className="form-select"
                                            name="client"
                                            value={form.client}
                                            onChange={handleClientChange}
                                        >
                                            <option value="">Select Client</option>
                                            {contacts.map((c) => (
                                                <option key={c._id} value={c._id}>
                                                    {c.name}
                                                </option>
                                            ))}
                                        </select>
                                        {form.client && (() => {
                                            const selClient = contacts.find((c) => String(c._id) === String(form.client));
                                            const cState = getClientState(selClient);
                                            return cState ? (
                                                <div className="text-muted small mt-1">
                                                    <i className="fas fa-map-marker-alt me-1 text-primary"></i>
                                                    State: <strong>{cState}</strong>
                                                </div>
                                            ) : null;
                                        })()}
                                    </div>

                                    {/* CONTACT NUMBER */}
                                    <div className="col-md-4">
                                        <label>Client Contact Number</label>
                                        <input
                                            type="text"
                                            className="form-control"
                                            value={form.clientContactNumber}
                                            readOnly
                                        />
                                    </div>

                                    {/* EMAIL */}
                                    <div className="col-md-4">
                                        <label>Client Email</label>
                                        <input
                                            type="email"
                                            className="form-control"
                                            value={form.clientEmail}
                                            readOnly
                                        />
                                    </div>
                                </div>

                                <div className="mb-3">
                                    <label>Project *</label>
                                    <select
                                        className="form-select"
                                        name="project"
                                        value={form.project}
                                        onChange={handleProjectChange}
                                    >
                                        <option value="">Select Project</option>
                                        {projects.map((p) => (
                                            <option key={p._id} value={p._id}>
                                                {p.projectCode} – {p.projectName}
                                            </option>
                                        ))}
                                    </select>
                                    {form.project && (() => {
                                        const selProj = projects.find((p) => String(p._id) === String(form.project));
                                        const off = getProjectOffice(selProj);
                                        return off ? (
                                            <div className="text-muted small mt-1">
                                                <i className="fas fa-building me-1 text-success"></i>
                                                Branch Office: <strong>{off.officeName || off.companyName}</strong> {off.state ? `(${off.state})` : ""}
                                            </div>
                                        ) : null;
                                    })()}
                                </div>
                                <div className="mb-3">
                                    <Row className="mt-2">
                                        <div className="mb-3">
                                            <label className="fw-bold">Scope of Work *</label>

                                            {loadingScopes ? (
                                                <div className="text-muted">Loading scope types...</div>
                                            ) : (
                                                <Row className="mt-2">
                                                    {scopeTypes.map((item) => (
                                                        <Col md={3} sm={6} key={item._id} className="mb-2">
                                                            <div className="form-check">
                                                                <input
                                                                    type="checkbox"
                                                                    className="form-check-input"
                                                                    id={`scope-${item._id}`}
                                                                    checked={form.scopeTypes.includes(item._id)}
                                                                    onChange={(e) => {
                                                                        if (e.target.checked) {
                                                                            setForm((prev) => ({
                                                                                ...prev,
                                                                                scopeTypes: [
                                                                                    ...prev.scopeTypes,
                                                                                    item._id,
                                                                                ],
                                                                            }));
                                                                        } else {
                                                                            setForm((prev) => ({
                                                                                ...prev,
                                                                                scopeTypes: prev.scopeTypes.filter(
                                                                                    (s) => s !== item._id,
                                                                                ),
                                                                            }));
                                                                        }
                                                                    }}
                                                                />
                                                                <label
                                                                    className="form-check-label"
                                                                    htmlFor={`scope-${item._id}`}
                                                                >
                                                                    {item.name}
                                                                </label>
                                                            </div>
                                                        </Col>
                                                    ))}
                                                </Row>
                                            )}
                                        </div>
                                    </Row>
                                </div>
                                {/* DESCRIPTION */}
                                <div>
                                    <label className="fw-bold">Scope Description *</label>
                                    <textarea
                                        name="scope"
                                        className="form-control"
                                        rows="4"
                                        placeholder="Describe detailed scope of work..."
                                        value={form.scope}
                                        onChange={handleChange}
                                    />
                                </div>

                                <label className="fw-bold">
                                    Project Value <span className="text-muted fw-normal small">(Optional — Auto-calculates from BOQ items)</span>
                                </label>

                                <Row className="align-items-start">
                                    {/* LEFT: INPUT & NON-GST SECTION */}
                                    <Col md={6}>
                                        <div className="input-group">
                                            <span className="input-group-text">₹</span>
                                            <input
                                                type="number"
                                                name="value"
                                                className="form-control"
                                                value={form.value}
                                                placeholder="0.00 (Auto-calculated from BOQ)"
                                                min="0"
                                                step="any"
                                                onWheel={(e) => e.target.blur()}
                                                onKeyDown={(e) => {
                                                    if (e.key === "-" || e.key === "e")
                                                        e.preventDefault();
                                                }}
                                                onChange={handleChange}
                                            />
                                        </div>

                                        {/* Crore / Lakh text */}
                                        {form.value && (
                                            <small className="text-muted d-block mt-1">
                                                {getIndianValueText(valueNumber)}
                                            </small>
                                        )}

                                        {/* 🔥 GST Not Include Amount Section */}
                                        <div className="mt-3 p-3 border rounded gst-not-included-card">
                                            <div className="d-flex justify-content-between align-items-center mb-2">
                                                <label className="fw-bold mb-0" style={{ fontSize: "14px" }}>
                                                    GST Not Include Amount
                                                </label>
                                                <button
                                                    type="button"
                                                    className="btn btn-xs btn-outline-primary"
                                                    onClick={handleAddGstNotIncluded}
                                                >
                                                    + Add Row
                                                </button>
                                            </div>
                                            <small className="text-muted d-block mb-2" style={{ fontSize: "12px" }}>
                                                Amount deducted from Project Value before calculating GST.
                                            </small>

                                            {gstNotIncluded.length === 0 ? (
                                                <div className="text-muted small fst-italic py-2 text-center border rounded gst-not-included-empty">
                                                    No excluded items added
                                                </div>
                                            ) : (
                                                <div className="d-flex flex-column gap-2">
                                                    {gstNotIncluded.map((item, index) => (
                                                        <Row key={item.id || index} className="g-2 align-items-center">
                                                            <Col xs={6}>
                                                                <input
                                                                    type="text"
                                                                    className="form-control form-control-sm"
                                                                    placeholder="Title (e.g. Security Deposit)"
                                                                    value={item.title}
                                                                    onChange={(e) =>
                                                                        handleGstNotIncludedChange(item.id, "title", e.target.value)
                                                                    }
                                                                />
                                                            </Col>
                                                            <Col xs={5}>
                                                                <div className="input-group input-group-sm">
                                                                    <span className="input-group-text">₹</span>
                                                                    <input
                                                                        type="number"
                                                                        className="form-control form-control-sm"
                                                                        placeholder="Amount"
                                                                        min="0"
                                                                        step="any"
                                                                        value={item.amount}
                                                                        onChange={(e) =>
                                                                            handleGstNotIncludedChange(item.id, "amount", e.target.value)
                                                                        }
                                                                    />
                                                                </div>
                                                            </Col>
                                                            <Col xs={1} className="text-end">
                                                                <button
                                                                    type="button"
                                                                    className="btn btn-sm btn-outline-danger p-0"
                                                                    style={{ width: "24px", height: "24px", lineHeight: "22px" }}
                                                                    title="Remove"
                                                                    onClick={() => handleRemoveGstNotIncluded(item.id)}
                                                                >
                                                                    &times;
                                                                </button>
                                                            </Col>
                                                        </Row>
                                                    ))}
                                                </div>
                                            )}

                                            {gstNotIncluded.length > 0 && (
                                                <div className="d-flex justify-content-between align-items-center mt-2 pt-2 border-top small">
                                                    <span className="text-muted fw-bold">Total Excluded (Non-GST):</span>
                                                    <span className="text-danger fw-bold">
                                                        - ₹ {formatIndianCurrency(
                                                            gstNotIncluded.reduce((acc, curr) => acc + (Number(curr.amount) || 0), 0)
                                                        )}
                                                    </span>
                                                </div>
                                            )}
                                        </div>
                                    </Col>


                                    <Col md={6}>
                                        <div className="border rounded p-3 bg-white shadow-sm">
                                            <div className="d-flex justify-content-between align-items-center mb-2 pb-1 border-bottom">
                                                <span className="small text-muted fw-bold">GST Type</span>
                                                <span className={`badge ${form.igst > 0 ? "bg-primary" : (form.cgst > 0 || form.sgst > 0) ? "bg-success" : "bg-secondary"}`}>
                                                    {form.igst > 0 ? "IGST (Inter-State 18%)" : (form.cgst > 0 || form.sgst > 0) ? "CGST + SGST (Intra-State 9% + 9%)" : "GST Pending"}
                                                </span>
                                            </div>

                                            <div className="mb-2 d-flex justify-content-between">
                                                <span>Project Value</span>
                                                <strong>₹ {formatIndianCurrency(valueNumber)}</strong>
                                            </div>

                                            {gstNotIncluded.length > 0 && (
                                                <div className="mb-2 d-flex justify-content-between text-danger">
                                                    <span>(-) GST Not Included</span>
                                                    <strong>
                                                        - ₹ {formatIndianCurrency(
                                                            gstNotIncluded.reduce((acc, curr) => acc + (Number(curr.amount) || 0), 0)
                                                        )}
                                                    </strong>
                                                </div>
                                            )}

                                            <div className="mb-2 d-flex justify-content-between text-primary fw-bold">
                                                <span>Taxable Base Sub Total</span>
                                                <strong>₹ {formatIndianCurrency(form.subTotal)}</strong>
                                            </div>

                                            <hr className="my-2" />

                                            <div className="mb-2 d-flex justify-content-between">
                                                <span>CGST ({form.cgst}%)</span>
                                                <strong>
                                                    ₹ {formatIndianCurrency(form.cgstAmount)}
                                                </strong>
                                            </div>

                                            <div className="mb-2 d-flex justify-content-between">
                                                <span>SGST ({form.sgst}%)</span>
                                                <strong>
                                                    ₹ {formatIndianCurrency(form.sgstAmount)}
                                                </strong>
                                            </div>

                                            <div className="mb-2 d-flex justify-content-between">
                                                <span>IGST ({form.igst}%)</span>
                                                <strong>
                                                    ₹ {formatIndianCurrency(form.igstAmount)}
                                                </strong>
                                            </div>

                                            <hr className="my-2" />

                                            <div className="d-flex justify-content-between">
                                                <span className="fw-bold">Total Work Order Amount</span>
                                                <strong className="fw-bold text-success fs-6">
                                                    ₹ {formatIndianCurrency(form.totalAmount)}
                                                </strong>
                                            </div>

                                            <small className="text-muted d-block mt-1">
                                                {getIndianValueText(form.totalAmount)}
                                            </small>
                                        </div>
                                    </Col>
                                </Row>

                                <div className="mb-3">
                                    <label>Status</label>
                                    <select
                                        name="status"
                                        className="form-select"
                                        value={form.status}
                                        onChange={handleChange}
                                    >
                                        <option value="Draft">Draft</option>
                                        <option value="Pending">Pending</option>
                                        <option value="In Progress">In Progress</option>
                                        <option value="Completed">Completed</option>
                                        <option value="Closed">Closed</option>
                                    </select>
                                </div>

                                <div className="row">
                                    <div className="col-md-4 mb-3">
                                        <label>Start Date *</label>
                                        <input
                                            type="date"
                                            name="startDate"
                                            className="form-control"
                                            value={form.startDate}
                                            onChange={handleChange}
                                        />
                                    </div>

                                    <div className="col-md-4 mb-3">
                                        <label>End Date *</label>
                                        <input
                                            type="date"
                                            name="endDate"
                                            className="form-control"
                                            value={form.endDate}
                                            onChange={handleChange}
                                        />
                                    </div>

                                    <div className="col-md-4 mb-3">
                                        <label>Total Days</label>
                                        <input
                                            type="text"
                                            className="form-control"
                                            value={calculateDays()}
                                            disabled
                                            placeholder="Auto calculated"
                                        />
                                    </div>
                                </div>
                                <h6 className="mt-4 mb-3 fw-bold">Execution Planning</h6>

                                <Row className="mb-3">
                                    {/* ESTIMATED DURATION */}
                                    <Col md={3}>
                                        <label>Estimated Duration (Days)</label>
                                        <input
                                            type="text"
                                            className="form-control"
                                            value={calculateDays()}
                                            readOnly
                                        />
                                    </Col>

                                    {/* ESTIMATED MANPOWER */}
                                    <Col md={3}>
                                        <label>Estimated Manpower</label>
                                        <input
                                            type="number"
                                            name="estimatedManpower"
                                            className="form-control"
                                            value={form.estimatedManpower}
                                            onChange={handleChange}
                                            min="0"
                                        />
                                    </Col>

                                    {/* PEAK MANPOWER */}
                                    <Col md={3}>
                                        <label>Peak Manpower</label>
                                        <input
                                            type="number"
                                            name="peakManpower"
                                            className="form-control"
                                            value={form.peakManpower}
                                            onChange={handleChange}
                                            min="0"
                                        />
                                    </Col>

                                    {/* MATERIAL BUDGET */}
                                    <Col md={3}>
                                        <label>Material Budget (₹)</label>
                                        <input
                                            type="number"
                                            name="materialBudget"
                                            className="form-control"
                                            value={form.materialBudget}
                                            onChange={handleChange}
                                            min="0"
                                            step="any"
                                        />
                                    </Col>
                                </Row>

                                <Row className="mb-3">
                                    {/* LABOUR BUDGET */}
                                    <Col md={3}>
                                        <label>Labour Budget (₹)</label>
                                        <input
                                            type="number"
                                            name="labourBudget"
                                            className="form-control"
                                            value={form.labourBudget}
                                            onChange={handleChange}
                                            min="0"
                                            step="any"
                                        />
                                    </Col>

                                    {/* CONTRACT BUDGET */}
                                    <Col md={3}>
                                        <label>Contract Budget (₹)</label>
                                        <input
                                            type="number"
                                            name="contractBudget"
                                            className="form-control"
                                            value={form.contractBudget}
                                            onChange={handleChange}
                                            min="0"
                                            step="any"
                                            placeholder="Internal contract execution cost"
                                        />
                                    </Col>

                                    {/* OTHER BUDGET */}
                                    <Col md={3}>
                                        <label>Other Expenses (₹)</label>
                                        <input
                                            type="number"
                                            name="otherBudget"
                                            className="form-control"
                                            value={form.otherBudget}
                                            onChange={handleChange}
                                            min="0"
                                            step="any"
                                            placeholder="Miscellaneous"
                                        />
                                    </Col>

                                    {/* OTHER BUDGET NOTE */}
                                    <Col md={3}>
                                        <label>Other Expenses Note</label>
                                        <input
                                            type="text"
                                            name="otherBudgetNote"
                                            className="form-control"
                                            value={form.otherBudgetNote}
                                            onChange={handleChange}
                                            placeholder="e.g. Admin, travel, etc."
                                        />
                                    </Col>
                                </Row>

                                {/* TOTAL BUDGET SUMMARY */}
                                <div className="border rounded p-3 mb-4">
                                    <h6 className="fw-bold mb-3">
                                        <i className="fas fa-calculator me-2 text-primary" />
                                        Budget Summary
                                    </h6>
                                    <Row className="row-cols-1 row-cols-sm-2 row-cols-md-5 g-3 align-items-stretch">
                                        {[
                                            { label: "Material Budget", val: form.materialBudget || 0 },
                                            { label: "Labour Budget", val: form.labourBudget || 0 },
                                            { label: "Contract Budget", val: form.contractBudget || 0 },
                                            { label: "Other Expenses", val: form.otherBudget || 0 },
                                        ].map((item) => (
                                            <Col key={item.label}>
                                                <div className="text-center h-100 px-2 py-2">
                                                    <small className="text-muted d-block">{item.label}</small>
                                                    <strong>
                                                        ₹ {Number(item.val).toLocaleString("en-IN")}
                                                    </strong>
                                                </div>
                                            </Col>
                                        ))}
                                        <Col>
                                            <div
                                                className="text-center rounded h-100 px-2 py-2"
                                                style={{ background: "#0078D718" }}
                                            >
                                                <small className="fw-bold text-primary d-block">
                                                    Total Budget
                                                </small>
                                                <strong className="text-primary">
                                                    ₹{" "}
                                                    {(
                                                        Number(form.materialBudget || 0) +
                                                        Number(form.labourBudget || 0) +
                                                        Number(form.contractBudget || 0) +
                                                        Number(form.otherBudget || 0)
                                                    ).toLocaleString("en-IN")}
                                                </strong>
                                            </div>
                                        </Col>
                                    </Row>
                                </div>

                                {/* 🔥 FIXED PROJECT MANAGER DROPDOWN */}
                                <div className="mb-3">
                                    <label>Project Manager</label>
                                    <select
                                        name="projectManager"
                                        className="form-select"
                                        value={form.projectManager}
                                        onChange={handleChange}
                                    >
                                        <option value="">Select PM</option>
                                        {users
                                            .filter((u) => {
                                                const r = (u.role?.name || "").toLowerCase().trim();
                                                return r === "manager" || r === "project manager";
                                            })
                                            .map((u) => (
                                                <option key={u._id} value={u._id}>
                                                    {u.name} {u.role?.name ? `(${u.role.name})` : ""}
                                                </option>
                                            ))}
                                    </select>
                                </div>

                                <WorkOrderUserAssignment
                                    users={users}
                                    selectedProjectId={form.project}
                                    selectedUserIds={form.engineerAssigned}
                                    onChange={(selectedIds) =>
                                        setForm((prev) => ({
                                            ...prev,
                                            engineerAssigned: selectedIds,
                                        }))
                                    }
                                />

                                <Card className="mb-3">
                                    <Card.Header className="d-flex justify-content-between align-items-center">
                                        <Card.Title className="mb-0">Imported BOQ</Card.Title>
                                        <div className="d-flex gap-2">
                                            {form.boqItems.length > 0 && (
                                                <Button
                                                    variant="outline-danger"
                                                    size="sm"
                                                    onClick={() =>
                                                        setForm((prev) => ({ ...prev, boqItems: [] }))
                                                    }
                                                >
                                                    Clear
                                                </Button>
                                            )}
                                            <Button
                                                variant="primary"
                                                size="sm"
                                                onClick={() => setShowBoqImport(true)}
                                            >
                                                Import BOQ PDF
                                            </Button>
                                        </div>
                                    </Card.Header>
                                    <Card.Body>
                                        {form.boqItems.length > 0 ? (
                                            <Table responsive bordered size="sm" className="mb-0">
                                                <thead className="table-light">
                                                    <tr>
                                                        <th>#</th>
                                                        <th>WLI</th>
                                                        <th>Item Name</th>
                                                        <th>Description</th>
                                                        <th>Unit</th>
                                                        <th>Qty</th>
                                                        <th>Rate</th>
                                                        <th>Total</th>
                                                    </tr>
                                                </thead>
                                                <tbody>
                                                    {form.boqItems.map((item, index) => (
                                                        <tr key={`${item.wlino || "row"}-${index}`}>
                                                            <td>{index + 1}</td>
                                                            <td>{item.wlino || "-"}</td>
                                                            <td>{item.itemName || "-"}</td>
                                                            <td>{item.description || "-"}</td>
                                                            <td>{item.unit || "-"}</td>
                                                            <td>{Number(item.quantity || 0)}</td>
                                                            <td>{Number(item.price || 0)}</td>
                                                            <td>{Number(item.quantity || 0) * Number(item.price || 0)}</td>
                                                        </tr>
                                                    ))}
                                                </tbody>
                                            </Table>
                                        ) : (
                                            <div className="text-muted text-center py-3">
                                                No BOQ rows imported yet.
                                            </div>
                                        )}
                                    </Card.Body>
                                </Card>

                                <Card className="mb-3">
                                    <Card.Header>
                                        <Card.Title>Documents</Card.Title>
                                    </Card.Header>

                                    <Card.Body>
                                        <input
                                            type="file"
                                            multiple
                                            className="form-control"
                                            accept=".pdf,.doc,.docx,.xls,.xlsx,.csv,.png,.jpg,.jpeg,.zip,.rar"
                                            onChange={handleFileUpload}
                                            disabled={loading}
                                        />

                                        {documents.length > 0 ? (
                                            <Table responsive bordered className="mt-3">
                                                <thead>
                                                    <tr>
                                                        <th>#</th>
                                                        <th>File</th>
                                                        <th>Document Type</th>
                                                        <th>Remarks</th>
                                                        <th>Action</th>
                                                    </tr>
                                                </thead>
                                                <tbody>
                                                    {documents.map((doc, index) => (
                                                        <tr key={doc.id}>
                                                            <td>{index + 1}</td>
                                                            <td>{doc.file.name}</td>
                                                            <td>
                                                                {loadingTypes ? (
                                                                    <span className="text-muted">
                                                                        Loading types...
                                                                    </span>
                                                                ) : (
                                                                    <select
                                                                        className="form-select form-select-sm"
                                                                        value={doc.documentType}
                                                                        onChange={(e) =>
                                                                            handleDocumentChange(
                                                                                doc.id,
                                                                                "documentType",
                                                                                e.target.value,
                                                                            )
                                                                        }
                                                                    >
                                                                        <option value="">Select Type</option>
                                                                        {documentTypes.map((t) => (
                                                                            <option key={t._id} value={t._id}>
                                                                                {t.name}
                                                                            </option>
                                                                        ))}
                                                                    </select>
                                                                )}
                                                            </td>

                                                            <td>
                                                                <input
                                                                    type="text"
                                                                    className="form-control form-control-sm"
                                                                    value={doc.remarks}
                                                                    onChange={(e) =>
                                                                        handleDocumentChange(
                                                                            doc.id,
                                                                            "remarks",
                                                                            e.target.value,
                                                                        )
                                                                    }
                                                                />
                                                            </td>
                                                            <td>
                                                                <div className="d-flex gap-2">
                                                                    <Button
                                                                        variant="info"
                                                                        size="sm"
                                                                        onClick={() => window.open(URL.createObjectURL(doc.file), "_blank")}
                                                                        title="Preview"
                                                                    >
                                                                        <i className="fas fa-eye"></i>
                                                                    </Button>
                                                                    <Button
                                                                        variant="danger"
                                                                        size="sm"
                                                                        onClick={() => handleRemoveDocument(doc.id)}
                                                                        title="Remove"
                                                                    >
                                                                        <i className="fas fa-trash"></i>
                                                                    </Button>
                                                                </div>
                                                            </td>
                                                        </tr>
                                                    ))}
                                                </tbody>
                                            </Table>
                                        ) : (
                                            <div className="text-muted text-center py-3">
                                                No documents uploaded yet.
                                            </div>
                                        )}
                                    </Card.Body>
                                </Card>

                                <button
                                    type="submit"
                                    className="btn btn-primary"
                                    disabled={loading}
                                >
                                    {loading ? "Creating..." : "Create Work Order"}
                                </button>
                            </form>
                        </Card.Body>
                    </Card>
                </Col>
            </Row>
            <BoqImportModal
                show={showBoqImport}
                onClose={() => setShowBoqImport(false)}
                onApply={handleApplyImportedBoq}
                sacCode={form.sacCode}
            />
        </Fragment>
    );
};

export default WorkOrderAdd;
